import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ErrorOutline } from '@mui/icons-material';

import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { CustomButton } from '../components/common/CustomButton.jsx';
import { CustomTextField } from '../components/common/CustomTextField.jsx';
import { LoadingSpinner } from '../components/common/LoadingSpinner.jsx';
import { useAuth } from '../hooks/useAuth.js';
import { getPhoneError, getPINError } from '../utils/validation.js';
import { Phone, Lock } from '@mui/icons-material';
import platformLogo from '../assets/imgx.png';

export const LoginPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isWide = useMediaQuery(theme.breakpoints.up('md'));
  const { login, isLoading, isAuthenticated } = useAuth();
  const [error, setError] = useState('');
  const [showError, setShowError] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      phone: '',
      pin: '',
    },
  });

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/home');
    }
  }, [isAuthenticated, navigate]);

  const onSubmit = async (data) => {
    try {
      setError('');
      setShowError(false);
      const cleanPhone = data.phone.replace(/\D/g, '');
      await login(cleanPhone, data.pin);
      navigate('/home');
    } catch (err) {
      const message = err.message || 'Login failed. Please try again.';
      // Map generic server messages to user-friendly ones
      const friendlyMessage =
        message.toLowerCase().includes('invalid') ||
        message.toLowerCase().includes('incorrect') ||
        message.toLowerCase().includes('wrong') ||
        message.toLowerCase().includes('credentials')
          ? 'Incorrect phone number or PIN. Please try again.'
          : message.includes('400')
            ? 'Incorrect phone number or PIN. Please try again.'
            : message;
      setError(friendlyMessage);
      setShowError(true);
    }
  };

  const handlePhoneChange = (value) => {
    const cleanValue = value.replace(/\D/g, '').slice(0, 10);
    setValue('phone', cleanValue, { shouldValidate: true });
  };

  if (isLoading) {
    return <LoadingSpinner fullScreen message="Logging in..." />;
  }

  const leftPanel = (
    <Box
      sx={{
        flex: isWide ? '2 1 0%' : 'none',
        minHeight: isWide ? '100vh' : 'auto',
        px: { xs: 3, sm: 4, md: 6 },
        py: { xs: 4, md: 6 },
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        bgcolor: '#FFFFFF',
        backgroundImage: `
          linear-gradient(128deg, rgba(69, 39, 160, 0.06) 0%, transparent 42%),
          linear-gradient(208deg, rgba(255, 107, 53, 0.05) 0%, transparent 48%),
          radial-gradient(ellipse 90% 70% at 0% 100%, rgba(69, 39, 160, 0.04) 0%, transparent 55%),
          radial-gradient(ellipse 60% 50% at 100% 0%, rgba(255, 107, 53, 0.03) 0%, transparent 50%)
        `,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background:
            'radial-gradient(circle at 85% 75%, rgba(69, 39, 160, 0.05) 0%, transparent 45%)',
        }}
      />
      <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 550 }}>
        <Box
          component="img"
          src={platformLogo}
          alt="Karobar"
          sx={{
            height: { xs: 150, md: 100 },
            width: 'auto',
            maxWidth: 250,
            objectFit: 'contain',
            mb: 1,
          }}
        />
        <Typography
          component="h1"
          sx={{
            fontWeight: 400,
            fontSize: { xs: '1.75rem', md: '2.25rem' },
            lineHeight: 1.15,
            letterSpacing: '-0.03em',
            color: '#1D1D1F',
            mb: 1,
          }}
        >
          restaurant business made easy.
        </Typography>
        {/* <Typography
          sx={{
            fontWeight: 100,
            fontSize: { xs: '0.55rem', md: '0.85rem' },
            lineHeight: 1.5,
            color: '#636366',
            maxWidth: 440,
          }}
        >
          Karobar Ai copyright © 2026. All rights reserved.
        </Typography> */}
      </Box>
    </Box>
  );

  const divider = (
    <Box
      sx={{
        flexShrink: 0,
        alignSelf: 'center',
        bgcolor: 'rgba(0, 0, 0, 0.06)',
        ...(isWide
          ? {
              width: '1px',
              height: 'calc(100vh - 96px)',
              minHeight: 320,
              my: 0,
            }
          : {
              width: 'calc(100% - 48px)',
              maxWidth: 360,
              height: '1px',
              my: 0,
              mx: 'auto',
            }),
      }}
    />
  );

  const formPanel = (
    <Box
      sx={{
        flex: isWide ? '1 1 0%' : 'none',
        minHeight: isWide ? '100vh' : 'auto',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        px: { xs: 3, sm: 5, md: 5 },
        py: { xs: 4, md: 6 },
        bgcolor: '#EBEBEF',
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 360, mx: 'auto' }}>
        <Typography
          sx={{
            fontWeight: 500,
            fontSize: '1.35rem',
            letterSpacing: '-0.02em',
            color: '#1D1D1F',
            mb: 0.5,
          }}
        >
          sign in
        </Typography>
        <Typography
          sx={{
            fontWeight: 300,
            fontSize: '0.875rem',
            color: '#86868B',
            mb: 3.5,
          }}
        >
          use your registered mobile number and PIN.
        </Typography>

        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2.5,
          }}
        >
          <Controller
            name="phone"
            control={control}
            rules={{
              required: 'Phone number is required',
              validate: (value) => {
                const err = getPhoneError(value);
                return err || true;
              },
            }}
            render={({ field }) => (
              <CustomTextField
                {...field}
                // label="Mobile number"
                placeholder="Mobile number"
                startIcon={<Phone sx={{ color: '#AEAEB2' }} />}
                onChange={(e) => handlePhoneChange(e.target.value)}
                onBlur={field.onBlur}
                error={!!errors.phone}
                errorMessage={errors.phone?.message}
                fullWidth
                inputProps={{
                  maxLength: 10,
                  inputMode: 'numeric',
                  autoComplete: 'tel',
                  'aria-invalid': !!errors.phone,
                }}
                sx={{
                  '& .MuiOutlinedInput-input': {
                    letterSpacing: '0.14em',
                    fontWeight: 600,
                    fontSize: '1rem',
                    fontVariantNumeric: 'tabular-nums',
                    color: '#1C1C1E',
                  },
                  '& .MuiInputLabel-root': {
                    fontWeight: 600,
                    color: '#636366',
                  },
                }}
              />
            )}
          />

          <Controller
            name="pin"
            control={control}
            rules={{
              required: 'PIN is required',
              validate: (value) => {
                const err = getPINError(value);
                return err || true;
              },
            }}
            render={({ field }) => (
              <CustomTextField
                {...field}
                // label="PIN"
                placeholder="PIN"
                type="password"
                startIcon={<Lock sx={{ color: '#AEAEB2' }} />}
                inputProps={{ maxLength: 6, inputMode: 'numeric', autoComplete: 'current-password' }}
                error={!!errors.pin}
                errorMessage={errors.pin?.message}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-input': {
                    fontWeight: 600,
                    letterSpacing: '0.12em',
                  },
                  '& .MuiInputLabel-root': {
                    fontWeight: 600,
                    color: '#636366',
                  },
                }}
              />
            )}
          />

          <CustomButton
            type="submit"
            variant="primary"
            fullWidth
            disabled={!isValid}
            loading={isLoading}
            sx={{
              mt: 1,
              py: 1.35,
              fontWeight: 800,
              fontSize: '0.95rem',
              borderRadius: '12px',
            }}
          >
            Sign in
          </CustomButton>
        </Box>

        <Typography
          sx={{
            mt: 3,
            fontSize: '0.8rem',
            fontWeight: 300,
            color: '#8E8E93',
            textAlign: 'center',
          }}
        >
          new here? ask your outlet admin to invite you.
        </Typography>

        <Typography
          onClick={() => navigate('/blog')}
          sx={{
            mt: 2,
            fontSize: '0.8rem',
            fontWeight: 500,
            color: '#4527A0',
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'color 0.2s',
            '&:hover': { color: '#FF6B35' },
          }}
        >
          Know more about Karobar →
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: isWide ? 'row' : 'column',
        alignItems: isWide ? 'stretch' : 'stretch',
      }}
    >
      {leftPanel}
      {divider}
      {formPanel}

      <Dialog
        open={showError}
        onClose={() => setShowError(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            pt: 3,
            pb: 1,
            fontWeight: 600,
            fontSize: '1.1rem',
            color: '#1C1C1E',
          }}
        >
          <ErrorOutline
            sx={{
              color: '#FF3B30',
              fontSize: '1.5rem',
            }}
          />
          Login Failed
        </DialogTitle>
        <DialogContent
          sx={{
            py: 2,
            color: '#636366',
            lineHeight: 1.6,
          }}
        >
          {error}
        </DialogContent>
        <DialogActions
          sx={{
            p: 2,
            pt: 1,
          }}
        >
          <CustomButton
            onClick={() => setShowError(false)}
            variant="primary"
            fullWidth
            sx={{
              py: 1,
              fontWeight: 700,
              borderRadius: '10px',
            }}
          >
            Try Again
          </CustomButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
